import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem as MenuItemAction,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogContent,
  Tooltip,
  Snackbar,
} from "@mui/material";
import {
  Search,
  MoreVert,
  Delete,
  Block,
  Close,
  ContentCopy,
} from "@mui/icons-material";
import { userService } from "../../services/userService";
import { subscriptionService } from "../../services/subscriptionService";
import { useNavigate, useLocation } from "react-router-dom";
import { getPageNumbers } from "../../utils/pagination";

const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  // const [categoryFilter, setCategoryFilter] = useState("All Category");
  const [subscribe, setSubscribe] = useState("All");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subAnchorEl, setSubAnchorEl] = useState(null);
  const [subMenuUser, setSubMenuUser] = useState(null);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [blockSnackbar, setBlockSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modal states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [coinsToAdd, setCoinsToAdd] = useState("");
  const [coinReason, setCoinReason] = useState("");
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Fetch users from API
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await userService.getUsers();

      if (result.success) {
        // Filter out admin users - only show users with role "user"
        const filteredUsers = result.data.filter(
          (user) => user.role === "user",
        );

        // Transform API data to match the component's expected format
        const transformedUsers = filteredUsers.map((user) => ({
          userId: user._id.$oid,
          userName: user.name || "-",
          dateOfJoining: user.created_at
            ? new Date(user.created_at.$date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "-",
          phone: user.phone || "",
          countryCode: user.country_code || "",
          email: user.email || "-",
          gender: user.gender || "-",
          placeOfBirth: user.place_of_birth || "-",
          dateOfBirth: user.date_of_birth || "-",
          timeOfBirth: user.time_of_birth || "-",
          isOnboarded: user.is_onboarded || false,
          status:
            user.is_enabled === false
              ? "Blocked"
              : user.is_onboarded
                ? "Active"
                : "Inactive",
          is_subscribed: user.is_subscribed ?? false,
          subUsers: Array.isArray(user.sub_users)
            ? user.sub_users.map((subUser) => ({
                id: subUser._id?.$oid || subUser._id || "",
                name: subUser.name || "Unnamed",
                dateOfBirth: subUser.date_of_birth || "N/A",
                timeOfBirth: subUser.time_of_birth || "N/A",
                placeOfBirth: subUser.place_of_birth || "N/A",
                gender: subUser.gender || "N/A",
              }))
            : [],
          subUsersCount: Array.isArray(user.sub_users)
            ? user.sub_users.length
            : 0,
          questions_asked_count: user?.questions_asked_count || 0,
          reports_generated_count: user?.reports_generated_count || 0,
          last_login_date:
            user?.last_login_date?.$date || user?.last_login_at?.$date
              ? new Date(
                  user?.last_login_date?.$date || user?.last_login_at?.$date,
                ).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "N/A",
        }));

        setUserData(transformedUsers);
        return transformedUsers;
      } else {
        setError(result.message);
        return [];
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users. Please try again.");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const isInitializingRef = React.useRef(true);

  useEffect(() => {
    // Initialize from URL query params (if present) and then load users — runs only on mount
    const params = new URLSearchParams(location.search);
    const s = params.get("search") || "";
    const status = params.get("status") || "All Status";
    const rawSub = params.get("subscribe");
    const subscribe =
      rawSub === "true" ? true : rawSub === "false" ? false : "All";
    const page = parseInt(params.get("page") || "1", 10) || 1;

    // Initialize state from URL params
    setSearchTerm(s);
    setStatusFilter(status);
    setSubscribe(subscribe);
    setCurrentPage(page);

    // load users and mark initialization complete afterwards
    loadUsers().finally(() => {
      isInitializingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleSubMenuClick = (event, user) => {
    // prevent parent row click handlers from firing
    event.stopPropagation();
    setSubMenuUser(user);
    setSubAnchorEl(event.currentTarget);
  };

  const handleSubMenuClose = () => {
    setSubAnchorEl(null);
    setSubMenuUser(null);
  };

  // Open delete confirmation dialog
  const handleDeleteUser = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedUser
  };

  // Confirm delete action
  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const result = await userService.deleteUser(selectedUser.userId);

      if (result.success) {
        // Reload users and get the fresh list
        const users = await loadUsers();

        // Apply current filters locally to compute pagination
        const searchLower = (searchTerm || "").toLowerCase();
        const normalizeDigits = (s) => (s || "").toString().replace(/\D/g, "");
        const queryDigits = normalizeDigits(searchTerm);

        const newFiltered = (users || []).filter((user) => {
          const nameMatch = (user.userName || "")
            .toLowerCase()
            .includes(searchLower);
          const idMatch = user.userId
            .toString()
            .toLowerCase()
            .includes(searchLower);
          const emailMatch = (user.email || "")
            .toLowerCase()
            .includes(searchLower);
          const placeMatch = (user.placeOfBirth || "")
            .toLowerCase()
            .includes(searchLower);

          const phoneDigits = normalizeDigits(user.phone);
          let phoneMatch = false;
          if (queryDigits) {
            phoneMatch =
              (phoneDigits && phoneDigits.includes(queryDigits)) ||
              (phoneDigits && queryDigits.includes(phoneDigits)) ||
              (phoneDigits && phoneDigits.endsWith(queryDigits)) ||
              (queryDigits && queryDigits.endsWith(phoneDigits));
          } else {
            phoneMatch = (user.phone || "").includes(searchTerm || "");
          }

          const matchesSearch =
            nameMatch || idMatch || phoneMatch || emailMatch || placeMatch;
          const matchesStatus =
            statusFilter === "All Status" || user.status === statusFilter;

          const matchesSubscribe =
            subscribe === "All" || user.is_subscribed === subscribe;
          return matchesSearch && matchesStatus && matchesSubscribe;
        });

        const newTotalPages = Math.max(
          1,
          Math.ceil(newFiltered.length / itemsPerPage),
        );

        // If current page is now out of range (e.g., last record deleted), move to previous page
        if (currentPage > newTotalPages) {
          setCurrentPage(newTotalPages);
        }

        setDeleteDialogOpen(false);
        setSelectedUser(null);
        setSuccess("User deleted successfully.");
        setTimeout(() => setSuccess(""), 3000);
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      alert("Failed to delete user. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  // Open block confirmation dialog
  const handleBlockUser = () => {
    setBlockDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedUser
  };

  // Confirm block/unblock action
  const handleConfirmBlock = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const isCurrentlyBlocked = selectedUser.status === "Blocked";
      const result = isCurrentlyBlocked
        ? await userService.unblockUser(selectedUser.userId)
        : await userService.blockUser(selectedUser.userId, true);

      if (result.success) {
        await loadUsers();
        setBlockDialogOpen(false);
        setBlockSnackbar({
          open: true,
          message: `User ${isCurrentlyBlocked ? "unblocked" : "blocked"} successfully`,
          severity: "success",
        });
        setSelectedUser(null);
      } else {
        setBlockSnackbar({
          open: true,
          message: result.message,
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Error blocking/unblocking user:", err);
      setBlockSnackbar({
        open: true,
        message: "Failed to block/unblock user. Please try again.",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel block action
  const handleCancelBlock = () => {
    setBlockDialogOpen(false);
    setSelectedUser(null);
  };

  const handleViewDetails = () => {
    if (selectedUser) {
      // Preserve current search / filter query when navigating to details
      navigate(`/users/${selectedUser.userId}${location.search}`, {
        state: {
          subUsers: selectedUser.subUsers || [],
          mainUserName: selectedUser.userName || selectedUser.name,
        },
      });
    }
    handleMenuClose();
  };

  const handleOpenUserDetails = async (user) => {
    setSelectedUserDetails(user);
    setUserDetailsOpen(true);
    setUserStats(null);
    setStatsLoading(true);
    try {
      const result = await userService.getUserStats(user.userId);
      if (result.success) {
        setUserStats(result.data);
      }
    } catch (err) {
      console.error("Error fetching user stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleCloseUserDetails = () => {
    setUserDetailsOpen(false);
    setSelectedUserDetails(null);
    setCoinsToAdd("");
    setCoinReason("");
  };

  const handleAddCoins = () => {
    if (!selectedUserDetails) return;

    setActionLoading(true);

    (async () => {
      try {
        const coinsNumber = Number(coinsToAdd);
        const result = await subscriptionService.addCoins(
          selectedUserDetails.userId,
          coinsNumber,
          coinReason,
        );

        if (result.success) {
          setSuccess(result.message || "Coins added successfully.");
          // refresh users list to reflect any changes
          await loadUsers();
          handleCloseUserDetails();
          setTimeout(() => setSuccess(""), 3000);
        } else {
          alert(result.message || "Failed to add coins.");
        }
      } catch (err) {
        console.error("Error adding coins:", err);
        alert("Failed to add coins. Please try again.");
      } finally {
        setActionLoading(false);
      }
    })();
  };

  const filteredUsers = userData.filter((user) => {
    const searchLower = (searchTerm || "").toLowerCase();
    const normalizeDigits = (s) => (s || "").toString().replace(/\D/g, "");
    const queryDigits = normalizeDigits(searchTerm);

    const nameMatch = (user.userName || "").toLowerCase().includes(searchLower);
    const idMatch = user.userId.toString().toLowerCase().includes(searchLower);
    const emailMatch = (user.email || "").toLowerCase().includes(searchLower);
    const placeMatch = (user.placeOfBirth || "")
      .toLowerCase()
      .includes(searchLower);

    const phoneDigits = normalizeDigits(user.phone);
    let phoneMatch = false;
    if (queryDigits) {
      phoneMatch =
        (phoneDigits && phoneDigits.includes(queryDigits)) ||
        (phoneDigits && queryDigits.includes(phoneDigits)) ||
        (phoneDigits && phoneDigits.endsWith(queryDigits)) ||
        (queryDigits && queryDigits.endsWith(phoneDigits));
    } else {
      phoneMatch = (user.phone || "").includes(searchTerm || "");
    }

    const matchesSearch =
      nameMatch || idMatch || phoneMatch || emailMatch || placeMatch;
    const matchesStatus =
      statusFilter === "All Status" || user.status === statusFilter;
    const matchesSubscribe =
      subscribe === "All" || user.is_subscribed === subscribe;
    return matchesSearch && matchesStatus && matchesSubscribe;
  });

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Get current page items for pagination
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };

  // Update total pages when filtered users change
  useEffect(() => {
    setTotalPages(Math.ceil(filteredUsers.length / itemsPerPage));
  }, [filteredUsers.length, itemsPerPage]);

  // Reset to first page when search/filter criteria change
  useEffect(() => {
    // Avoid resetting when we're initializing from URL params
    if (isInitializingRef.current) return;
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Persist search, filters and page in URL so navigating away and back retains state
  useEffect(() => {
    // Debounce updating the URL to avoid reloading users on every keystroke
    const handler = setTimeout(() => {
      const params = new URLSearchParams();
      if (searchTerm) params.set("search", searchTerm);
      if (statusFilter && statusFilter !== "All Status")
        params.set("status", statusFilter);
      if (subscribe !== "All") params.set("subscribe", subscribe);
      if (currentPage && currentPage > 1)
        params.set("page", String(currentPage));

      const query = params.toString();
      const target = query
        ? `${location.pathname}?${query}`
        : location.pathname;
      // replace current history entry so back button behavior is preserved
      navigate(target, { replace: true });
    }, 500);

    return () => clearTimeout(handler);
  }, [
    searchTerm,
    statusFilter,
    subscribe,
    currentPage,
    location.pathname,
    navigate,
  ]);

  // Focus the alert when a success message appears (accessibility)
  const alertRef = useRef(null);
  useEffect(() => {
    if (success && alertRef.current) {
      // focus to make screen readers announce it
      alertRef.current.focus();
    }
  }, [success]);

  return (
    <Box sx={{ backgroundColor: "#000000", minHeight: "100%", width: "100%" }}>
      {/* Alerts - Full Width */}
      <Box sx={{ marginBottom: 3 }}>
        {success && (
          <Alert
            severity="success"
            icon={false}
            onClose={() => setSuccess("")}
            ref={alertRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            sx={{
              backgroundColor: "#4caf50",
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 24px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0,
              },
            }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            icon={false}
            onClose={() => setError(null)}
            sx={{
              backgroundColor: "#f44336",
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 24px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0,
              },
            }}
          >
            {error}
          </Alert>
        )}
      </Box>

      {/* Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h4"
          sx={{
            color: "#FFFFFF",
            fontWeight: 600,
            marginBottom: 1,
            fontSize: { xs: "24px", md: "32px" },
          }}
        >
          Manage Users
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#888888",
            fontSize: "16px",
          }}
        >
          Manage user accounts, status, and access.
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          marginBottom: 4,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        {/* Search Field */}
        <TextField
          placeholder="Search users"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#666666" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            maxWidth: { xs: "100%", md: "400px" },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "25px",
              "& fieldset": {
                borderColor: "#E0E0E0",
              },
              "&:hover fieldset": {
                borderColor: "#E1AD01",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#E1AD01",
              },
            },
            "& .MuiInputBase-input": {
              padding: "12px 14px",
              color: "#333333",
              "&::placeholder": {
                color: "#666666",
                opacity: 1,
              },
            },
          }}
        />

        {/* Status Filter */}
        <Box sx={{ minWidth: 150 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#1A1A1A",
                  border: "1px solid #333333",
                  borderRadius: "8px",
                  maxHeight: 300,
                  zIndex: 9999,
                  "& .MuiMenuItem-root": {
                    color: "#FFFFFF",
                    backgroundColor: "#1A1A1A",
                    fontSize: "14px",
                    padding: "12px 16px",
                    "&:hover": {
                      backgroundColor: "#333333",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#E1AD01",
                      color: "#000000",
                      "&:hover": {
                        backgroundColor: "#B8941F",
                      },
                    },
                  },
                },
              },
            }}
            sx={{
              backgroundColor: "#1A1A1A",
              color: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #333333",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-icon": {
                color: "#FFFFFF",
              },
            }}
          >
            <MenuItem value="All Status">All Status</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
            <MenuItem value="Blocked">Blocked</MenuItem>
          </Select>
        </Box>

        {/* Category Filter */}
        <Box sx={{ minWidth: 150 }}>
          <Select
            value={subscribe}
            onChange={(e) => setSubscribe(e.target.value)}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#1A1A1A",
                  border: "1px solid #333333",
                  borderRadius: "8px",
                  maxHeight: 300,
                  zIndex: 9999,
                  "& .MuiMenuItem-root": {
                    color: "#FFFFFF",
                    backgroundColor: "#1A1A1A",
                    fontSize: "14px",
                    padding: "12px 16px",
                    "&:hover": {
                      backgroundColor: "#333333",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#E1AD01",
                      color: "#000000",
                      "&:hover": {
                        backgroundColor: "#B8941F",
                      },
                    },
                  },
                },
              },
            }}
            sx={{
              backgroundColor: "#1A1A1A",
              color: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #333333",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-icon": {
                color: "#FFFFFF",
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value={true}>Subscribe</MenuItem>
            <MenuItem value={false}>Unsubscribe</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* User Details Table */}
      <Box
        sx={{ backgroundColor: "#1A1A1A", borderRadius: "16px", padding: 3 }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#FFFFFF",
            fontWeight: 600,
            marginBottom: 3,
            fontSize: "20px",
          }}
        >
          User Details
        </Typography>

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress sx={{ color: "#E1AD01" }} />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        {!loading && !error && (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              boxShadow: "none",
              overflowX: "auto",
            }}
          >
            <Table sx={{ minWidth: 650, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    User ID
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    User Name
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Sub Users
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Date of Joining
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Contact Info
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Questions Asked
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Reports Downloaded
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Last Login
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Status
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 600, color: "#666666", fontSize: "14px" }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getCurrentPageItems().length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ textAlign: "center", color: "#666666", padding: 4 }}
                    >
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  getCurrentPageItems().map((user, index) => (
                    <TableRow
                      key={index}
                      sx={{ "&:hover": { backgroundColor: "#F8F9FA" } }}
                    >
                      <TableCell sx={{ color: "#333333", fontSize: "14px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title={user.userId} arrow placement="top">
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333333",
                                cursor: "default",
                              }}
                            >
                              {user.userId.substring(0, 8)}...
                            </Typography>
                          </Tooltip>
                          <Tooltip title="Copy User ID" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(user.userId);
                                setCopySnackbar(true);
                              }}
                              sx={{
                                padding: "4px",
                                color: "#999",
                                "&:hover": { color: "#E1AD01" },
                              }}
                            >
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#2196F3",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor: "pointer",
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                        title={user.userName}
                        onClick={() => handleOpenUserDetails(user)}
                      >
                        {user.userName}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "14px",
                          cursor:
                            user.subUsersCount > 0 ? "pointer" : "default",
                        }}
                        onClick={
                          user.subUsersCount > 0
                            ? (e) => handleSubMenuClick(e, user)
                            : undefined
                        }
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              color:
                                user.subUsersCount > 0 ? "#2196F3" : "#666666",
                              fontWeight: 600,
                            }}
                          >
                            {user.subUsersCount}
                          </Box>
                          {user.subUsersCount > 0 && (
                            <Box sx={{ fontSize: "12px", color: "#000000" }}>
                              ▼
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "#333333", fontSize: "14px" }}>
                        {user.dateOfJoining}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333333",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        <Tooltip
                          title={
                            user.phone
                              ? `${user.countryCode} ${user.phone}`
                              : user.email
                          }
                          arrow
                          placement="top"
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333333",
                              cursor: "default",
                            }}
                          >
                            {user.phone
                              ? `${user.countryCode} ${user.phone}`
                              : user.email?.length > 8
                                ? `${user.email.substring(0, 8)}...`
                                : user.email}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333333",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {user.questions_asked_count}
                      </TableCell>{" "}
                      <TableCell
                        sx={{
                          color: "#333333",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {user.reports_generated_count}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333333",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {user.last_login_date}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            padding: "4px 12px",
                            borderRadius: "12px",
                            backgroundColor:
                              user.status === "Active"
                                ? "#E8F5E9"
                                : user.status === "Blocked"
                                  ? "#FFEBEE"
                                  : "#FFF3E0",
                            color:
                              user.status === "Active"
                                ? "#4CAF50"
                                : user.status === "Blocked"
                                  ? "#c62828"
                                  : "#FF9800",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {user.status}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, user)}
                          sx={{
                            color: "#666666",
                            "&:hover": { backgroundColor: "#F0F0F0" },
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {!loading && !error && filteredUsers.length > itemsPerPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              marginTop: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              sx={{
                borderColor: "#666666",
                color: "#CCCCCC",
                borderRadius: "20px",
                padding: "8px 20px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#E1AD01",
                  backgroundColor: "rgba(212, 175, 55, 0.1)",
                },
                "&:disabled": {
                  borderColor: "#333333",
                  color: "#666666",
                },
              }}
            >
              Previous
            </Button>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                page === "..." ? (
                  <Box
                    key={`ellipsis-${idx}`}
                    sx={{ color: "#999999", fontSize: "14px", px: 0.5 }}
                  >
                    ...
                  </Box>
                ) : (
                  <Box
                    key={page}
                    onClick={() => handlePageChange(page)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor:
                        page === currentPage ? "#E1AD01" : "#333333",
                      color: page === currentPage ? "#000000" : "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          page === currentPage ? "#B8941F" : "#444444",
                      },
                    }}
                  >
                    {page}
                  </Box>
                ),
              )}
            </Box>

            <Button
              variant="contained"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              sx={{
                backgroundColor: "#E1AD01",
                color: "#000000",
                borderRadius: "20px",
                padding: "8px 20px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#B8941F",
                },
                "&:disabled": {
                  backgroundColor: "#333333",
                  color: "#666666",
                },
              }}
            >
              Next
            </Button>
          </Box>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              minWidth: 180,
              border: "1px solid #E0E0E0",
              marginTop: "8px",
            },
          }}
        >
          <MenuItemAction
            onClick={handleDeleteUser}
            sx={{
              color: "#333333",
              fontSize: "14px",
              padding: "12px 20px",
              "&:hover": {
                backgroundColor: "#F5F5F5",
              },
              borderBottom: "1px solid #F0F0F0",
            }}
          >
            Delete User
          </MenuItemAction>
          <MenuItemAction
            onClick={handleBlockUser}
            sx={{
              color: "#333333",
              fontSize: "14px",
              padding: "12px 20px",
              "&:hover": {
                backgroundColor: "#F5F5F5",
              },
              borderBottom: "1px solid #F0F0F0",
            }}
          >
            {selectedUser?.status === "Blocked" ? "Unblock User" : "Block User"}
          </MenuItemAction>
          <MenuItemAction
            onClick={handleViewDetails}
            sx={{
              color: "#333333",
              fontSize: "14px",
              padding: "12px 20px",
              backgroundColor: "#F8F9FA",
              "&:hover": {
                backgroundColor: "#E9ECEF",
              },
            }}
          >
            View Details
          </MenuItemAction>
        </Menu>

        {/* Sub Users Menu (opens when clicking the sub-users cell) */}
        <Menu
          anchorEl={subAnchorEl}
          open={Boolean(subAnchorEl)}
          onClose={handleSubMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              minWidth: 220,
              padding: 0,
              boxShadow: "0px 8px 30px rgba(0,0,0,0.12)",
              backgroundColor: "#FFFFFF",
            },
          }}
        >
          {subMenuUser?.subUsers?.length ? (
            subMenuUser.subUsers.map((subUser) => (
              <MenuItemAction
                key={subUser.id || subUser.name}
                sx={{ fontSize: 16, padding: "14px 20px", color: "#333333" }}
                onClick={() => {
                  if (!subUser.id) return;
                  navigate(
                    `/users/${subMenuUser.userId}?profile_id=${subUser.id}`,
                    {
                      state: {
                        subUsers: subMenuUser.subUsers || [],
                        mainUserName: subMenuUser.userName || subMenuUser.name,
                      },
                    },
                  );
                  handleSubMenuClose();
                }}
              >
                {subUser.name}
              </MenuItemAction>
            ))
          ) : (
            <MenuItemAction
              sx={{ fontSize: 16, padding: "14px 20px", color: "#666666" }}
              onClick={handleSubMenuClose}
            >
              No sub users
            </MenuItemAction>
          )}
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#FFFFFF",
              borderRadius: "20px",
              padding: "8px",
              maxWidth: "450px",
            },
          }}
        >
          <DialogContent
            sx={{ padding: "40px 32px 32px 32px", textAlign: "center" }}
          >
            {/* Delete Icon */}
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: "#FFEBEE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <Delete sx={{ fontSize: 32, color: "#F44336" }} />
            </Box>

            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: 600,
                color: "#333333",
                marginBottom: 2,
              }}
            >
              Delete User?
            </Typography>

            <Typography
              sx={{
                fontSize: "16px",
                color: "#666666",
                marginBottom: 4,
                lineHeight: 1.5,
              }}
            >
              Are you sure you want to delete this user? This action is
              permanent.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancelDelete}
                disabled={actionLoading}
                sx={{
                  borderColor: "#E0E0E0",
                  color: "#666666",
                  backgroundColor: "transparent",
                  borderRadius: "25px",
                  padding: "12px 32px",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "16px",
                  minWidth: "120px",
                  "&:hover": {
                    borderColor: "#CCCCCC",
                    backgroundColor: "#F5F5F5",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#E0E0E0",
                    color: "#999999",
                    cursor: "not-allowed",
                    pointerEvents: "auto",
                  },
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleConfirmDelete}
                disabled={actionLoading}
                sx={{
                  backgroundColor: "#F44336",
                  color: "#FFFFFF",
                  borderRadius: "25px",
                  padding: "12px 32px",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "16px",
                  minWidth: "120px",
                  "&:hover": { backgroundColor: "#D32F2F" },
                  "&:disabled": {
                    backgroundColor: "#FFCDD2",
                    color: "#FFFFFF",
                  },
                }}
              >
                {actionLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#FFF" }} />
                    Deleting...
                  </Box>
                ) : (
                  "Delete"
                )}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Block Confirmation Dialog */}
        <Dialog
          open={blockDialogOpen}
          onClose={handleCancelBlock}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#FFFFFF",
              borderRadius: "20px",
              padding: "8px",
              maxWidth: "450px",
            },
          }}
        >
          <DialogContent
            sx={{ padding: "40px 32px 32px 32px", textAlign: "center" }}
          >
            {/* Block Icon */}
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: "#FFF3E0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <Block sx={{ fontSize: 32, color: "#FF9800" }} />
            </Box>

            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: 600,
                color: "#333333",
                marginBottom: 2,
              }}
            >
              {selectedUser?.status === "Blocked"
                ? "Unblock User?"
                : "Block User?"}
            </Typography>

            <Typography
              sx={{
                fontSize: "16px",
                color: "#666666",
                marginBottom: 4,
                lineHeight: 1.5,
              }}
            >
              {selectedUser?.status === "Blocked"
                ? "Unblock this user and restore their access to the app?"
                : "Block this user from accessing the app? You can unblock anytime."}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancelBlock}
                sx={{
                  borderColor: "#E0E0E0",
                  color: "#666666",
                  backgroundColor: "transparent",
                  borderRadius: "25px",
                  padding: "12px 32px",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "16px",
                  minWidth: "120px",
                  "&:hover": {
                    borderColor: "#CCCCCC",
                    backgroundColor: "#F5F5F5",
                  },
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleConfirmBlock}
                disabled={actionLoading}
                sx={{
                  backgroundColor: "#FF9800",
                  color: "#FFFFFF",
                  borderRadius: "25px",
                  padding: "12px 32px",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "16px",
                  minWidth: "120px",
                  "&:hover": { backgroundColor: "#F57C00" },
                  "&:disabled": {
                    backgroundColor: "#FFE0B2",
                    color: "#FFFFFF",
                  },
                }}
              >
                {actionLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#FFF" }} />
                    {selectedUser?.status === "Blocked"
                      ? "Unblocking..."
                      : "Blocking..."}
                  </Box>
                ) : selectedUser?.status === "Blocked" ? (
                  "Unblock"
                ) : (
                  "Block"
                )}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* User Details Modal */}
        <Dialog
          open={userDetailsOpen}
          onClose={handleCloseUserDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#FFFFFF",
              borderRadius: "24px",
              padding: "0",
              maxWidth: "800px",
              maxHeight: "90vh",
            },
          }}
          sx={{
            "& .MuiDialog-container": {
              alignItems: "center",
            },
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent
            sx={{
              padding: "40px",
              position: "relative",
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "calc(90vh - 80px)",
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseUserDetails}
              sx={{
                position: "absolute",
                right: 20,
                top: 20,
                color: "#666666",
                "&:hover": { backgroundColor: "#F5F5F5" },
              }}
            >
              <Close />
            </IconButton>

            {/* Title */}
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 600,
                color: "#000000",
                marginBottom: 3,
                textAlign: "left",
              }}
            >
              User Details
            </Typography>

            {selectedUserDetails && (
              <Box>
                {/* User Info Section */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    marginBottom: 3,
                  }}
                >
                  {/* Profile Picture */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: "#E0E0E0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "32px",
                        fontWeight: 600,
                        color: "#666666",
                      }}
                    >
                      {selectedUserDetails.userName.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>

                  {/* User Name and Details */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 600,
                        color: "#000000",
                        marginBottom: 0.5,
                      }}
                    >
                      {selectedUserDetails.userName}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#666666" }}>
                      {selectedUserDetails.dateOfBirth} |{" "}
                      {selectedUserDetails.placeOfBirth}
                    </Typography>
                  </Box>
                </Box>

                {/* Stats Section */}
                <Box
                  sx={{
                    backgroundColor: "#FEE38F",
                    borderRadius: "50px",
                    padding: "15px",
                    marginBottom: 3,
                    display: "flex",
                    justifyContent: "space-around",
                    gap: 2,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#000000",
                        marginBottom: 0.5,
                      }}
                    >
                      {statsLoading
                        ? "..."
                        : userStats
                          ? `$${userStats.total_spent}`
                          : "--"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#666666" }}>
                      Total Amount Spent
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#000000",
                        marginBottom: 0.5,
                      }}
                    >
                      {statsLoading
                        ? "..."
                        : userStats
                          ? userStats.coins
                          : "--"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#666666" }}>
                      Available Coins
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#000000",
                        marginBottom: 0.5,
                      }}
                    >
                      {statsLoading
                        ? "..."
                        : userStats
                          ? userStats.reports_generated
                          : "--"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#666666" }}>
                      Report Generated
                    </Typography>
                  </Box>
                </Box>

                {/* Add Coins Section */}
                <Box sx={{ marginBottom: 2, marginTop: 4 }}>
                  <Box sx={{ position: "relative", marginBottom: 4 }}>
                    <Typography
                      sx={{
                        position: "absolute",
                        top: -10,
                        left: 20,
                        backgroundColor: "#E1AD01",
                        color: "#000000",
                        padding: "4px 16px",
                        borderRadius: "12px",
                        fontSize: "14px",
                        fontWeight: 600,
                        zIndex: 1,
                      }}
                    >
                      Add coins
                    </Typography>
                    <TextField
                      placeholder="No. of coins"
                      value={coinsToAdd}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e") {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        let val = e.target.value;

                        if (val === "") {
                          setCoinsToAdd("");
                          return;
                        }

                        const num = Number(val);

                        if (!isNaN(num)) {
                          if (num > 1000) val = 1000;
                          if (num < 1) val = 1;

                          setCoinsToAdd(val);
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      slotProps={{ htmlInput: { min: 1 } }}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#F5F5F5",
                          borderRadius: "16px",
                          paddingTop: "12px",
                          "& fieldset": {
                            borderColor: "#E1AD01",
                          },
                          "&:hover fieldset": {
                            borderColor: "#E1AD01",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#E1AD01",
                          },
                        },
                        "& .MuiInputBase-input": {
                          padding: "14px 16px",
                          color: "#333333",
                          "&::placeholder": {
                            color: "#999999",
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ position: "relative" }}>
                    <Typography
                      sx={{
                        position: "absolute",
                        top: -10,
                        left: 20,
                        backgroundColor: "#E1AD01",
                        color: "#000000",
                        padding: "4px 16px",
                        borderRadius: "12px",
                        fontSize: "14px",
                        fontWeight: 600,
                        zIndex: 1,
                      }}
                    >
                      Reason
                    </Typography>
                    <Select
                      value={coinReason}
                      onChange={(e) => setCoinReason(e.target.value)}
                      displayEmpty
                      fullWidth
                      sx={{
                        backgroundColor: "#F5F5F5",
                        borderRadius: "16px",
                        paddingTop: "12px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E0E0E0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E1AD01",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E1AD01",
                        },
                        "& .MuiSelect-select": {
                          padding: "14px 16px",
                          color: coinReason ? "#333333" : "#999999",
                        },
                        "& .MuiSelect-icon": {
                          color: "#666666",
                        },
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        disableScrollLock: true,
                        disablePortal: false,
                        PaperProps: {
                          sx: {
                            backgroundColor: "#FFFFFF",
                            borderRadius: "12px",
                            marginTop: "8px",
                            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                            maxHeight: "120px",
                            overflowY: "auto",
                            "& .MuiMenuItem-root": {
                              color: "#333333",
                              fontSize: "14px",
                              padding: "12px 16px",
                              "&:hover": {
                                backgroundColor: "#F5F5F5",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "#FFF9E6",
                                "&:hover": {
                                  backgroundColor: "#FFF3D6",
                                },
                              },
                            },
                            "& .MuiList-root": {
                              paddingTop: 0,
                              paddingBottom: 0,
                            },
                          },
                        },
                        MenuListProps: {
                          sx: {
                            paddingTop: "8px",
                            paddingBottom: "8px",
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Reason
                      </MenuItem>
                      <MenuItem value="bonus">Bonus</MenuItem>
                      <MenuItem value="refund">Refund</MenuItem>
                      <MenuItem value="promotion">Promotion</MenuItem>
                      <MenuItem value="compensation">Compensation</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    marginTop: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCloseUserDetails}
                    fullWidth
                    sx={{
                      borderColor: "#E1AD01",
                      color: "#E1AD01",
                      backgroundColor: "transparent",
                      borderRadius: "50px",
                      padding: "14px 40px",
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "18px",
                      "&:hover": {
                        borderColor: "#B8941F",
                        backgroundColor: "rgba(204, 158, 73, 0.1)",
                      },
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleAddCoins}
                    disabled={!coinsToAdd || !coinReason || actionLoading}
                    fullWidth
                    sx={{
                      backgroundColor: "#E1AD01",
                      color: "#FFFFFF",
                      borderRadius: "50px",
                      padding: "14px 40px",
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "18px",
                      "&:hover": { backgroundColor: "#B8941F" },
                      "&:disabled": {
                        backgroundColor: "#E0E0E0",
                        color: "#999999",
                      },
                    }}
                  >
                    {actionLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                        Setting...
                      </Box>
                    ) : (
                      "Set"
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: "80px" }}
      >
        <Alert
          onClose={() => setCopySnackbar(false)}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            fontWeight: 500,
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            "& .MuiAlert-action .MuiIconButton-root": { color: "#fff" },
          }}
        >
          Copied to clipboard
        </Alert>
      </Snackbar>
      <Snackbar
        open={blockSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setBlockSnackbar({ ...blockSnackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: "80px" }}
      >
        <Alert
          onClose={() => setBlockSnackbar({ ...blockSnackbar, open: false })}
          severity={blockSnackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            fontWeight: 500,
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            "& .MuiAlert-action .MuiIconButton-root": { color: "#fff" },
          }}
        >
          {blockSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
